import { useState, useCallback } from "react";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { PrimaryButton } from "@normasteaching/norma-ui";
import { Currency } from "../../../ProductService/NormasTeachingProduct/useNormasTeachingProductById.ts";
import { NormasTeachingCheckoutFormData } from "../../CheckoutData.ts";

function mapCurrencyToSymbol(currency: Currency): string {
  switch (currency) {
    case Currency.Eur:
      return "€";
    default:
      return currency;
  }
}

export function CardPaymentForm({
  checkoutData,
  orderInfo,
  onSuccess,
  onError,
  onBack,
}: {
  checkoutData: NormasTeachingCheckoutFormData;
  orderInfo: {
    normasTeachingItemName: string;
    amountCurrency: { amount: number; currency: string };
    returnUrl?: string;
  };
  onSuccess: () => void;
  onError: (message: string) => void;
  onBack: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [confirming, setConfirming] = useState(false);

  const handleError = useCallback(
    (message: string) => {
      onError(message);
    },
    [onError],
  );

  const onConfirmPayment = async () => {
    if (!stripe || !elements) {
      handleError(
        "Errore nel caricamento di Stripe. Riprova tra qualche secondo.",
      );
      return;
    }

    setConfirming(true);
    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: orderInfo.returnUrl },
        redirect: "if_required",
      });

      if (error) {
        handleError(error.message || "Pagamento non riuscito.");
      } else {
        onSuccess();
      }
    } catch (err) {
      handleError("Errore durante la conferma del pagamento.");
    } finally {
      setConfirming(false);
    }
  };

  function checkoutPriceWIthCoupon(amount: number) {
    return checkoutData.coupon ? amount - checkoutData.coupon.value : amount;
  }

  return (
    <div className="max-w-2xl mx-auto p-6 bg-white">
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Completa il pagamento
          </h2>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-lg font-medium text-gray-900">
              {orderInfo.normasTeachingItemName}
            </div>
            {checkoutData.coupon ? (
              <>
                <div className="text-gray-500 line-through">
                  {(orderInfo.amountCurrency.amount / 100).toFixed(2)}{" "}
                  {mapCurrencyToSymbol(
                    orderInfo.amountCurrency.currency as Currency,
                  )}
                </div>
                <div className="text-xl font-bold text-gray-600">
                  {(
                    checkoutPriceWIthCoupon(orderInfo.amountCurrency.amount) /
                    100
                  ).toFixed(2)}{" "}
                  {mapCurrencyToSymbol(
                    orderInfo.amountCurrency.currency as Currency,
                  )}
                </div>
                {/*<div className="ml-auto bg-green-100 text-green-800 text-sm font-semibold px-3 py-1 rounded-full">*/}
                {/*  Coupon applicato:{" "}*/}
                {/*  {checkoutData.coupon.couponCode.toUpperCase()}*/}
                {/*</div>*/}
              </>
            ) : (
              <div className="text-gray-900 font-semibold">
                Totale: {(orderInfo.amountCurrency.amount / 100).toFixed(2)}{" "}
                {mapCurrencyToSymbol(
                  orderInfo.amountCurrency.currency as Currency,
                )}
              </div>
            )}
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg">
          <h3 className="font-medium text-gray-900 mb-2">Dati acquirente</h3>
          <p className="text-sm text-gray-700">
            {checkoutData.customer.firstName} {checkoutData.customer.lastName}
          </p>
          <p className="text-sm text-gray-700">{checkoutData.customer.email}</p>
        </div>

        <div className="w-full">
          <label className="block text-md font-bold text-gray-900 mb-3">
            Dati della carta
          </label>
          <div className="w-full [&_.__PrivateStripeElement]:max-w-full">
            <PaymentElement
              options={{
                layout: {
                  type: "accordion",
                  defaultCollapsed: false,
                  radios: true,
                  spacedAccordionItems: false,
                },
                wallets: {
                  applePay: "auto",
                  googlePay: "auto",
                },
              }}
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 pt-6">
          <PrimaryButton
            label={
              confirming ? "Elaborazione in corso..." : "Completa il pagamento"
            }
            onClick={onConfirmPayment}
            disabled={!stripe || !elements || confirming}
            loading={confirming}
          />

          <button
            type="button"
            onClick={onBack}
            disabled={confirming}
            className="text-gray-600 hover:text-gray-900 underline text-sm"
          >
            Torna indietro
          </button>
        </div>
      </div>
    </div>
  );
}
